<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';

import MilestoneRecoveryForm from './MilestoneRecoveryForm.vue';
import PlanRelayDialog from './PlanRelayDialog.vue';
import StatusChip from './StatusChip.vue';
import { eqsApi } from '@/api/eqs';
import { useSession } from '@/auth/session';
import { notify } from '@/services/notice';
import type { HealthStatus, Milestone, PlanSummary, Project, ProjectHealth } from '@/types/domain';
import { errorMessage, formatDate } from '@/utils/format';
import { healthLabel as lampLabel } from '@/utils/portfolio';
import { PROJECT_TYPES } from '@/utils/project-types';

const props = defineProps<{
  project: Project;
  health?: ProjectHealth;
  summary?: PlanSummary;
  milestones: Milestone[];
  projects: Project[];
  asOf: string;
}>();

/** declared：这里改了东西（自评、挽回计划、项目类型、铺了计划），外面要重新拉一次健康度 */
const emit = defineEmits<{ close: []; declared: [] }>();

const healthLabel = computed(() => lampLabel(props.health?.status));

/*
  项目经理自评（0911）：系统按事实算地板，自评只能往上。
  这里只给能写的项目显示表单；谁能填在后端拦（本项目项目经理 / 管理员 / director）。
*/
const session = useSession();
const declareStatus = ref<HealthStatus>(props.health?.declaration?.status ?? props.health?.status ?? 'normal');
const declareReason = ref('');
const declaring = ref(false);
const declareError = ref('');
// 会话结构老的浏览器里 canWrite 可能不存在：没有就不显示表单，后端照样拦
const canDeclare = computed(() => Boolean(session.canWrite?.value) && props.project.writable !== false);
/*
  抽屉开着时健康度会变（0930 起能就地补类型、铺计划）：单选要跟着当前灯走，
  不然灯已经绿了、单选还停在打开时的红灯，顺手一点「记录自评」就把项目标红了。
  人已经开始写原因时不动他的选择。
*/
watch(
  () => props.health?.declaration?.status ?? props.health?.status,
  (next) => {
    if (!declareReason.value.trim()) declareStatus.value = next ?? 'normal';
  },
);

/*
  就地处理要知道「这个人能不能改这个项目 / 能不能改计划」（0930）。
  项目页传进来的项目带着这两个字段；组合页、指挥台传的是组合快照里的项目，没有「能不能改计划」——
  那就取一次项目详情，口径和写入拦截同一个方法（projects.controller 的 writable / planWritable）。
*/
const detail = ref<{ writable?: boolean; planWritable?: boolean; projectManager?: string | null } | null>(null);
const canEditProject = computed(() => Boolean(session.canWrite?.value)
  && (detail.value?.writable ?? props.project.writable) !== false);
const canSeedPlan = computed(() => Boolean(session.canWrite?.value)
  && (detail.value?.planWritable ?? props.project.planWritable) === true);
const projectManagerName = computed(() => detail.value?.projectManager ?? props.project.projectManager ?? null);

async function loadAccess(): Promise<void> {
  if (props.project.planWritable !== undefined && props.project.writable !== undefined) return;
  try {
    const loaded = await eqsApi.projectDetail(props.project.id);
    detail.value = { writable: loaded.writable, planWritable: loaded.planWritable, projectManager: loaded.projectManager };
  } catch {
    // 取不到就按「不能改」显示指引文字，不给控件；后端照样拦
    detail.value = { writable: false, planWritable: false };
  }
}

async function submitDeclaration(): Promise<void> {
  if (declaring.value) return;
  if (!declareReason.value.trim()) { declareError.value = '自评必须写原因：领导看到的是原因，不是颜色。'; return; }
  declaring.value = true;
  declareError.value = '';
  try {
    await eqsApi.declareProjectStatus(props.project.id, { status: declareStatus.value, reason: declareReason.value.trim() });
    notify('success', `已记录自评：${lampLabel(declareStatus.value)}`, '系统会拿它和事实取重；比事实轻的自评会被标出来。');
    declareReason.value = '';
    emit('declared');
  } catch (caught) {
    declareError.value = errorMessage(caught);
  } finally {
    declaring.value = false;
  }
}

/* ---------- 就地处理（0930 Tony：「能否直接在健康度里面操作，现在有些点击进去不知道要做啥」） ---------- */

/** 项目类型：就在卡片上选、保存；保存后外面重新算健康度，这张卡自己消失 */
const projectTypeDraft = ref('');
const savingType = ref(false);
const typeError = ref('');
const typeSaved = ref('');
async function saveProjectType(): Promise<void> {
  if (savingType.value) return;
  if (!projectTypeDraft.value) { typeError.value = '先选一个项目类型。'; return; }
  savingType.value = true;
  typeError.value = '';
  try {
    await eqsApi.updateProject(props.project.id, { projectType: projectTypeDraft.value });
    typeSaved.value = projectTypeDraft.value;
    notify('success', `项目类型已保存：${projectTypeDraft.value}`, '健康度里「项目类型未维护」这一条已经消掉。');
    emit('declared');
  } catch (caught) {
    typeError.value = errorMessage(caught);
  } finally {
    savingType.value = false;
  }
}

/** 自评那张卡：表单就在下面，滚过去，不用再跳页面 */
const declareSection = ref<HTMLElement | null>(null);
function focusDeclare(): void {
  declareSection.value?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  declareSection.value?.querySelector('textarea')?.focus({ preventScroll: true });
}

const projectMilestones = computed(() => props.milestones.filter(
  (milestone) => String(milestone.projectId) === String(props.project.id),
));
/** 挽回日期不早于核对日 = 这个逾期有计划兜着（和 project-rag 的 milestoneDelayFacts 同一句话） */
function hasLiveRecovery(milestone: Milestone): boolean {
  const recovery = String(milestone.recoveryDate ?? '').slice(0, 10);
  return Boolean(recovery) && recovery >= props.asOf.slice(0, 10);
}
const overdueMilestones = computed(() => projectMilestones.value.filter(
  (milestone) => (
    milestone.planDate !== null
    && milestone.actualDate === null
    && milestone.planDate < props.asOf
  ),
));
const duplicateProjects = computed(() => props.projects.filter(
  (project) => (
    project.projectNo === props.project.projectNo
    && String(project.id) !== String(props.project.id)
  ),
));

/**
 * 一张卡片 = 三句话（0930 Tony：「点击进去不知道要做啥，有点混乱；项目类型未维护具体需要维护哪些信息，
 * 都不知道怎么关闭」）：
 * - evidence：为什么亮——系统看到的事实；
 * - todo：怎么处理——一句话说清要做什么，能就地做的就在卡片上给控件（inline）；
 * - closes：什么时候消掉——做到哪一步这张卡自己消失。
 * 要去别的页面处理的给 link，按钮写「打开 xx」，不写「进入 xx 补齐 xx」这种没说做什么的话。
 */
interface HealthFinding {
  key: string;
  level: 'risk' | 'warning';
  title: string;
  evidence: string;
  todo: string;
  closes: string;
  inline?: 'project-type' | 'seed-plan' | 'declare';
  link?: { label: string; route: string };
}

const findings = computed<HealthFinding[]>(() => {
  const result: HealthFinding[] = [];
  const reasons = new Set(props.health?.reasons || []);
  const dataIssues = new Set(props.health?.dataIssues || []);
  const base = `/projects/${props.project.id}`;

  const facts = props.health?.issueFacts ?? null;
  if (reasons.has('issue_at_top') || reasons.has('issue_at_functional')) {
    const top = facts?.issuesAtTop ?? 0;
    const functional = facts?.issuesAtFunctional ?? 0;
    result.push({
      key: 'issue_level',
      level: reasons.has('issue_at_top') ? 'risk' : 'warning',
      title: reasons.has('issue_at_top') ? `${top} 条问题已升到管理层` : `${functional} 条问题已升到部门负责人`,
      evidence: `未关闭的问题里，最高升到了「${reasons.has('issue_at_top') ? '管理层' : '部门负责人'}」这一层`
        + (top && functional ? `（管理层 ${top} 条、部门负责人 ${functional} 条）` : '') + '。',
      todo: '到问题清单处理这几条：解决了就关闭；没那么严重的，降回项目层。',
      closes: '这些问题都关闭或降回项目层后自动消失，灯跟着回落。',
      link: { label: '打开问题清单', route: `${base}/issues` },
    });
  }
  if (reasons.has('customer_escalation') || reasons.has('customer_complaint')) {
    result.push({
      key: 'customer_complaint',
      level: reasons.has('customer_escalation') ? 'risk' : 'warning',
      title: reasons.has('customer_escalation') ? '有客户升级投诉' : '有客户口头 / 书面投诉',
      evidence: '未关闭的问题里，有「客户投诉」一栏不是「无」的。',
      todo: '到问题清单处理带投诉的问题，处理完关闭。',
      closes: '带投诉的问题都关闭后自动消失。',
      link: { label: '打开问题清单', route: `${base}/issues` },
    });
  }
  if (reasons.has('declared_risk') || reasons.has('declared_warning')) {
    const declaration = props.health?.declaration;
    result.push({
      key: 'declared',
      level: reasons.has('declared_risk') ? 'risk' : 'warning',
      title: `项目经理自评${reasons.has('declared_risk') ? '红灯' : '黄灯'}`,
      evidence: declaration
        ? `${declaration.declaredByName || '项目经理'} ${formatDate(declaration.declaredAt)}：${declaration.reason}`
        : '自评比系统按事实算出的颜色重，按自评定色。',
      todo: canDeclare.value
        ? '这是项目经理自己标的颜色。情况好转了，在下面「项目经理自评」里重新选一次、写上原因。'
        : '这是项目经理自己标的颜色，由本项目的项目经理在这里重新自评。',
      closes: '重新自评成「绿灯」后消失，灯回到按事实算的颜色。',
      inline: canDeclare.value ? 'declare' : undefined,
    });
  }
  if (reasons.has('missing_plan')) {
    const manager = projectManagerName.value;
    result.push({
      key: 'missing_plan',
      level: 'risk',
      title: '项目计划尚未配置',
      evidence: '这个项目一个计划节点都没有（多半是老系统迁过来时没带计划），阶段进度、责任人、交付日期都算不出来。',
      todo: canSeedPlan.value
        ? '点下面「按标准模板铺计划」：先预览会铺多少活动，确认后一次铺好，阶段日期按项目起止日期排。'
          + '项目本身、已有的里程碑、文件、BOM 都不动；铺完到阶段工作里调日期、定负责人。'
        : manager
          ? `只有本项目的项目经理（${manager}）或管理员能铺计划：请他打开这里，点「按标准模板铺计划」。`
          : '这个项目的团队里还没有项目经理。先到团队页配上项目经理，再由他在这里铺计划。',
      closes: '计划铺好后自动消失。',
      inline: canSeedPlan.value ? 'seed-plan' : undefined,
      link: !canSeedPlan.value && !manager ? { label: '打开项目团队', route: `${base}/team` } : undefined,
    });
  }
  /*
    0923 Tony 按准则改：里程碑逾期分黄红看的是「有没有挽回计划」，不是逾期几个。
    没登记 = 红；登记了、挽回日期没过 = 黄；挽回日期也过了 = 回到红。
    登记就在下面每个里程碑那一行上做，不用再跳去别的页面。
  */
  if (reasons.has('milestone_overdue_no_plan') || reasons.has('milestone_overdue_with_plan')) {
    const unplanned = overdueMilestones.value.filter((milestone) => !hasLiveRecovery(milestone)).length;
    result.push({
      key: 'overdue_milestones',
      level: reasons.has('milestone_overdue_no_plan') ? 'risk' : 'warning',
      title: `${overdueMilestones.value.length} 个里程碑已经逾期`
        + (unplanned ? `，${unplanned} 个没有挽回计划` : '，都登记了挽回计划'),
      evidence: overdueMilestones.value.length
        ? `截至 ${formatDate(props.asOf)}，计划日期已过、还没有实际完成日期。`
          + '没登记挽回计划的按红算；登记了、挽回日期还没过的按黄算。'
        : '健康度返回了逾期标记，但当前里程碑明细未能匹配，需核对数据关联。',
      todo: '在下面每个逾期的里程碑上登记挽回计划（挽回到哪天、怎么补）；其实已经完成的，到计划甘特填上实际日期。',
      closes: '填了实际日期就不算逾期；都登记了挽回计划会降成黄灯，挽回日期过了还没完成又会变回红灯。',
      link: { label: '打开计划甘特', route: `${base}/plan` },
    });
  }
  // 0911 起「没关键交付物」「编号重复」是数据卫生，从 dataIssues 读，不定色
  if (reasons.has('no_key_deliverables') || dataIssues.has('no_key_deliverables')) {
    result.push({
      key: 'no_key_deliverables',
      level: 'warning',
      title: '计划中没有关键交付物',
      evidence: `计划里有 ${props.summary?.nodeCount ?? 0} 个节点，但一个都没勾「需评审」（关键交付物），`
        + 'Gate 评审没有资料门槛。',
      todo: '到阶段工作，在要交资料给评审的任务那一行，把「需评审」点上。',
      // 这一条读的是计划汇总缓存，每半小时补算一次——不说清楚，人勾完没见消失会以为没勾上
      closes: '至少有一个任务勾上「需评审」后消失；这一项的统计每半小时刷新一次，最晚半小时后看得到。',
      link: { label: '打开阶段工作', route: `${base}/activities` },
    });
  }
  if (reasons.has('duplicate_project_no') || dataIssues.has('duplicate_project_no')) {
    const duplicateNames = duplicateProjects.value
      .map((project) => `${project.projectName}（内部ID ${project.id}）`)
      .join('、');
    result.push({
      key: 'duplicate_project_no',
      level: 'warning',
      title: `项目编号 ${props.project.projectNo} 存在重复`,
      evidence: duplicateNames
        ? `另有：${duplicateNames}。系统靠内部 ID 区分两个项目，按项目编号关联的文件和任务可能挂错。`
        : '健康度返回了编号重复标记，但当前项目列表未能匹配另一条记录。',
      todo: '先核对哪个是对的：项目编号填错了，在模块中心的项目清单里改成正确的；多建了一个，请管理员把多余的那个归档。',
      closes: '项目编号不再重复后自动消失。',
      link: { label: '打开模块中心的项目清单', route: '/modules/projects' },
    });
  }
  if (dataIssues.has('missing_project_type')) {
    result.push({
      key: 'missing_project_type',
      level: 'warning',
      title: '项目类型未维护',
      evidence: '项目类型空着。它说明这个项目的产品设计由谁负责：自主设计（我们负责设计）、非自主设计（按客户的设计生产）。'
        + '新建项目时必选，老系统迁过来的项目都没有这一项。',
      todo: canEditProject.value
        ? '在下面选一个，点「保存」。'
        : '只有本项目的成员能改：请项目经理打开这里，选好保存。',
      closes: '保存后自动消失。',
      inline: canEditProject.value ? 'project-type' : undefined,
    });
  }

  // 1006 离职交接：责任人账号已停用、名下还挂着活。催办发给他等于发给空气，必须有人接
  if (dataIssues.has('departed_owner')) {
    result.push({
      key: 'departed_owner',
      level: 'warning',
      title: '有离职人员名下的活没接手',
      evidence: '本项目里有未完成交付物或在跑活动的责任人账号已停用（离职或被停用）。'
        + '系统已经不再给他们发催办，这些活在他们名下只会一直挂着。',
      todo: '到交付物页勾选他名下的行「批量设责任人」，阶段工作里把活动责任人改掉；'
        + '团队页里把他的角色撤掉，审批流才不会继续派给他。接手人是谁由项目经理定。',
      closes: '名下没有未完成交付物和在跑活动后自动消失（组合页每次打开现算）。',
      link: { label: '打开交付物页', route: `${base}/deliverables` },
    });
  }

  for (const reason of reasons) {
    if (![
      'missing_plan',
      'milestone_overdue_no_plan',
      'milestone_overdue_with_plan',
      'no_key_deliverables',
      'duplicate_project_no',
      'issue_at_top', 'issue_at_functional', 'customer_escalation', 'customer_complaint',
      'declared_risk', 'declared_warning', 'declaration_below_facts',
    ].includes(reason)) {
      result.push({
        key: `reason:${reason}`,
        level: 'warning',
        title: '存在尚未配置中文说明的健康度异常',
        evidence: `接口原因代码：${reason}`,
        todo: '系统还没给这种情况写说明，请把这张卡截图发给管理员。',
        closes: '',
      });
    }
  }
  for (const issue of dataIssues) {
    if (!['missing_project_type', 'no_key_deliverables', 'duplicate_project_no', 'departed_owner'].includes(issue)) {
      result.push({
        key: `data:${issue}`,
        level: 'warning',
        title: '存在尚未配置中文说明的数据问题',
        evidence: `接口数据问题代码：${issue}`,
        todo: '系统还没给这种情况写说明，请把这张卡截图发给管理员。',
        closes: '',
      });
    }
  }
  return result;
});

function close() {
  emit('close');
}

function onKeydown(event: KeyboardEvent) {
  if (event.key !== 'Escape') return;
  // 上面还开着「按标准模板铺计划」的对话框：Esc 不该把整个健康度抽屉一起关掉
  if (document.querySelector('.relay-mask')) return;
  close();
}

let previousBodyOverflow = '';
onMounted(() => {
  previousBodyOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';
  window.addEventListener('keydown', onKeydown);
  void loadAccess();
});
onBeforeUnmount(() => {
  document.body.style.overflow = previousBodyOverflow;
  window.removeEventListener('keydown', onKeydown);
});
</script>

<template>
  <Teleport to="body">
    <div class="health-detail-backdrop" @click.self="close">
      <section
        class="health-detail-panel"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="`health-detail-title-${project.id}`"
      >
        <header>
          <div>
            <span>PROJECT HEALTH · {{ project.projectNo }}</span>
            <h2 :id="`health-detail-title-${project.id}`">{{ project.projectName }}健康度明细</h2>
            <p>核对时点：{{ formatDate(asOf) }}。每一条都写了为什么亮、怎么处理、什么时候消掉；能在这里办的，卡片上直接给了按钮。</p>
          </div>
          <button type="button" aria-label="关闭项目健康度明细" @click="close">×</button>
        </header>

        <div class="health-detail-summary">
          <div>
            <span>当前灯</span>
            <StatusChip :value="health?.status || 'neutral'" :label="healthLabel" />
          </div>
          <div v-if="health?.floor && health.floor !== health.status">
            <span>事实地板</span>
            <StatusChip :value="health.floor" :label="lampLabel(health.floor)" />
          </div>
          <div><span>待处理</span><strong>{{ findings.length }}</strong></div>
          <div><span>逾期里程碑</span><strong>{{ overdueMilestones.length }}</strong></div>
          <div><span>计划节点</span><strong>{{ summary?.nodeCount ?? 0 }}</strong></div>
        </div>

        <div v-if="findings.length" class="health-finding-list">
          <article v-for="finding in findings" :key="finding.key" :class="`is-${finding.level}`" :data-finding="finding.key">
            <i aria-hidden="true">!</i>
            <div>
              <h3>{{ finding.title }}</h3>
              <p class="finding-evidence">{{ finding.evidence }}</p>
              <dl class="finding-steps">
                <div><dt>怎么处理</dt><dd>{{ finding.todo }}</dd></div>
                <div v-if="finding.closes"><dt>什么时候消掉</dt><dd>{{ finding.closes }}</dd></div>
              </dl>
              <ul v-if="finding.key === 'overdue_milestones' && overdueMilestones.length">
                <li v-for="milestone in overdueMilestones" :key="milestone.id">
                  <RouterLink
                    :to="{ path: `/projects/${project.id}/plan`, query: { gate: milestone.milestoneCode } }"
                    @click="close"
                  >
                    <b>{{ milestone.milestoneCode }}</b>
                    <span>计划 {{ formatDate(milestone.planDate) }}</span>
                    <small>{{ milestone.remark || milestone.gateStatus || '未维护补充说明' }}</small>
                  </RouterLink>
                  <MilestoneRecoveryForm
                    :project-id="project.id"
                    :milestone="milestone"
                    :today="asOf"
                    :editable="canDeclare"
                    @saved="emit('declared')"
                  />
                </li>
              </ul>

              <!-- 项目类型：就地选、保存（0930） -->
              <form
                v-if="finding.inline === 'project-type'"
                class="finding-inline"
                @submit.prevent="saveProjectType"
              >
                <label>
                  <span>项目类型</span>
                  <select v-model="projectTypeDraft" aria-label="项目类型" :disabled="savingType || Boolean(typeSaved)">
                    <option value="" disabled>请选择</option>
                    <option v-for="type in PROJECT_TYPES" :key="type.value" :value="type.value">
                      {{ type.value }}（{{ type.hint }}）
                    </option>
                  </select>
                </label>
                <button class="button button-primary" type="submit" :disabled="savingType || Boolean(typeSaved)">
                  {{ savingType ? '保存中…' : typeSaved ? '已保存' : '保存' }}
                </button>
                <small v-if="typeSaved" class="finding-ok">已保存为「{{ typeSaved }}」，正在刷新健康度…</small>
                <small v-if="typeError" class="finding-error">{{ typeError }}</small>
              </form>

              <!-- 没有计划：就地按标准模板铺（先预览再确认，和阶段工作页的「重新铺设计划」是同一个） -->
              <div v-if="finding.inline === 'seed-plan'" class="finding-inline">
                <PlanRelayDialog :project="project" label="按标准模板铺计划" teleport @done="emit('declared')" />
              </div>

              <div v-if="finding.inline === 'declare'" class="finding-inline">
                <button class="button button-secondary" type="button" @click="focusDeclare">去下面重新自评</button>
              </div>

              <RouterLink v-if="finding.link" class="health-finding-action" :to="finding.link.route" @click="close">
                {{ finding.link.label }} →
              </RouterLink>
            </div>
          </article>
        </div>
        <div v-else class="health-detail-normal">
          <b>当前未识别到健康度异常</b>
          <p>计划、里程碑、关键交付物和基础项目数据均未触发当前规则。</p>
        </div>

        <section v-if="health?.declarationBelowFacts" class="health-declaration-mismatch">
          <b>自评与事实不符</b>
          <p>
            {{ health.declaration?.declaredByName || '项目经理' }} {{ formatDate(health.declaration?.declaredAt || '') }}
            自评「{{ lampLabel(health.declaration?.status) }}」，但按事实算是「{{ lampLabel(health.floor) }}」——按事实定色。
            原因：{{ health.declaration?.reason }}
          </p>
        </section>

        <section v-if="canDeclare" ref="declareSection" class="health-declare">
          <h3>项目经理自评</h3>
          <p>只能往上调：觉得比系统算的更严重可以标红或标黄并写原因；想回到绿灯只能去把问题关掉、把计划补上。</p>
          <div class="health-declare-row">
            <label v-for="option in (['risk', 'warning', 'normal'] as const)" :key="option" :class="{ picked: declareStatus === option }">
              <input v-model="declareStatus" type="radio" name="declare-status" :value="option">
              <StatusChip :value="option" :label="lampLabel(option)" />
            </label>
          </div>
          <textarea v-model="declareReason" rows="2" placeholder="原因（必填）：例如客户已口头表示要换供应商，系统里还没有对应问题。"></textarea>
          <p v-if="declareError" class="health-declare-error">{{ declareError }}</p>
          <div class="health-declare-actions">
            <small v-if="health?.declaration">
              上次自评：{{ lampLabel(health.declaration.status) }} · {{ health.declaration.declaredByName || '' }} {{ formatDate(health.declaration.declaredAt) }}
            </small>
            <button type="button" class="button button-primary" :disabled="declaring" @click="submitDeclaration">记录自评</button>
          </div>
        </section>

        <footer>
          <small>颜色只由交付（计划 / 里程碑）和问题（升级层级 / 客户投诉）决定；不替代正式 Gate 评审。</small>
          <RouterLink :to="`/projects/${project.id}/overview`" class="button button-primary" @click="close">
            打开项目工作区
          </RouterLink>
        </footer>
      </section>
    </div>
  </Teleport>
</template>

<style scoped>
.health-declaration-mismatch { padding: 10px 14px; border: 1px solid #f2a39c; background: #fff5f4; border-radius: 4px; }
.health-declaration-mismatch b { color: var(--red); }
.health-declaration-mismatch p { margin: 4px 0 0; font-size: 13px; }
.health-declare { display: grid; gap: 8px; padding: 12px 14px; border: 1px solid var(--line); border-radius: 4px; background: var(--surface); }
.health-declare h3 { margin: 0; font-size: 14px; }
.health-declare p { margin: 0; color: var(--muted); font-size: 12px; }
.health-declare-row { display: flex; gap: 8px; flex-wrap: wrap; }
.health-declare-row label { display: inline-flex; align-items: center; gap: 6px; padding: 4px 8px; border: 1px solid var(--line); border-radius: 4px; background: var(--white); cursor: pointer; }
.health-declare-row label.picked { border-color: var(--blue); }
.health-declare textarea { font: inherit; font-size: 13px; padding: 6px 8px; border: 1px solid var(--line-strong); border-radius: 4px; }
.health-declare-error { color: var(--red) !important; font-weight: 700; }
.health-declare-actions { display: flex; justify-content: space-between; align-items: center; gap: 10px; }
.health-declare-actions small { color: var(--muted); font-size: 12px; }

.health-detail-backdrop {
  position: fixed;
  z-index: 120;
  inset: 0;
  display: grid;
  justify-items: end;
  background: rgba(16, 39, 56, .58);
  backdrop-filter: blur(3px);
}

.health-detail-panel {
  width: min(680px, 94vw);
  height: 100vh;
  padding: clamp(22px, 3vw, 38px);
  overflow-y: auto;
  border-left: 4px solid var(--blue);
  color: var(--ink);
  background: var(--paper);
  box-shadow: -24px 0 72px rgba(0, 0, 0, .22);
}

.health-detail-panel > header {
  display: flex;
  justify-content: space-between;
  gap: 22px;
}

.health-detail-panel > header span {
  color: var(--blue);
  font: 800 10px var(--display);
  letter-spacing: .13em;
}

.health-detail-panel h2 {
  margin: 8px 0 8px;
  font: 700 clamp(30px, 4vw, 44px)/1 var(--display);
}

.health-detail-panel > header p {
  margin: 0;
  color: var(--muted);
  font-size: 13px;
  line-height: 1.6;
}

.health-detail-panel > header button {
  flex: 0 0 42px;
  width: 42px;
  height: 42px;
  border: 1px solid var(--line);
  color: var(--ink);
  background: var(--white);
  cursor: pointer;
  font-size: 26px;
}

.health-detail-summary {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  margin-top: 24px;
  border: 1px solid var(--line);
  background: var(--white);
}

.health-detail-summary > div {
  min-height: 82px;
  display: grid;
  align-content: center;
  gap: 7px;
  padding: 12px;
  border-left: 1px solid var(--line);
}

.health-detail-summary > div:first-child { border-left: 0; }
.health-detail-summary span { color: var(--muted); font-size: 12px; }
.health-detail-summary strong { font: 700 28px var(--display); }

.health-finding-list {
  display: grid;
  gap: 10px;
  margin-top: 18px;
}

.health-finding-list > article {
  display: grid;
  grid-template-columns: 34px 1fr;
  gap: 12px;
  padding: 18px;
  border: 1px solid var(--line);
  border-left: 4px solid var(--warning);
  background: var(--white);
}

.health-finding-list > article.is-risk { border-left-color: var(--risk); }
.health-finding-list > article > i {
  width: 30px;
  height: 30px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  color: white;
  background: var(--warning);
  font-style: normal;
  font-weight: 800;
}
.health-finding-list > article.is-risk > i { background: var(--risk); }
.health-finding-list h3 { margin: 2px 0 6px; font: 700 19px var(--display); }
.health-finding-list p { margin: 0; color: var(--muted); font-size: 13px; line-height: 1.55; }
.health-finding-list ul { margin: 13px 0 0; padding: 0; list-style: none; }
.health-finding-list li { border-top: 1px solid var(--line); }
.health-finding-list li a {
  display: grid;
  grid-template-columns: 86px 112px 1fr;
  gap: 10px;
  align-items: center;
  min-height: 46px;
  font-size: 12px;
}
.health-finding-list li b { color: var(--risk); }
.health-finding-list li span,
.health-finding-list li small { color: var(--muted); }
/* 去别的页面办的入口：带边框的小按钮（可点的东西要有边框） */
.health-finding-action {
  min-height: 32px;
  display: inline-flex;
  align-items: center;
  margin-top: 12px;
  padding: 0 12px;
  border: 1px solid var(--blue);
  border-radius: var(--radius-s);
  color: var(--blue);
  background: var(--white);
  font-size: 13px;
  font-weight: 800;
  text-decoration: none;
}
.health-finding-action:hover { background: #f1f6fd; }

/* 为什么亮 / 怎么处理 / 什么时候消掉（0930） */
.finding-steps { display: grid; gap: 6px; margin: 12px 0 0; padding: 10px 12px; border-radius: var(--radius-s); background: var(--paper); }
.finding-steps > div { display: grid; grid-template-columns: 84px 1fr; gap: 8px; align-items: baseline; }
.finding-steps dt { color: var(--muted); font-size: 12px; font-weight: 700; }
.finding-steps dd { margin: 0; color: var(--ink); font-size: 13px; line-height: 1.6; }
.finding-inline { display: flex; flex-wrap: wrap; align-items: flex-end; gap: 10px; margin-top: 12px; }
.finding-inline label { display: grid; gap: 4px; }
.finding-inline label span { color: var(--muted); font-size: 12px; }
.finding-inline select { min-width: 240px; min-height: 36px; padding: 0 10px; border: 1px solid var(--line-strong); border-radius: var(--radius-s); background: var(--white); font: inherit; font-size: 14px; }
.finding-ok { flex-basis: 100%; color: var(--green); font-size: 12px; }
.finding-error { flex-basis: 100%; color: var(--risk); font-size: 12px; font-weight: 700; }

.health-detail-normal {
  margin-top: 18px;
  padding: 26px;
  border-left: 4px solid var(--green);
  background: rgba(38, 122, 99, .08);
}
.health-detail-normal b { font: 700 20px var(--display); }
.health-detail-normal p { margin: 6px 0 0; color: var(--muted); font-size: 13px; }

.health-detail-panel > footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin-top: 24px;
  padding-top: 18px;
  border-top: 1px solid var(--line);
}
.health-detail-panel > footer small { color: var(--muted); font-size: 12px; line-height: 1.5; }

@media (max-width: 600px) {
  .finding-steps > div { grid-template-columns: 1fr; gap: 2px; }
  .finding-inline select { min-width: 0; width: 100%; }
  .health-detail-summary { grid-template-columns: repeat(2, 1fr); }
  .health-detail-summary > div:nth-child(3) { border-left: 0; }
  .health-detail-summary > div:nth-child(n+3) { border-top: 1px solid var(--line); }
  .health-finding-list li a { grid-template-columns: 1fr; gap: 2px; padding: 8px 0; }
  .health-detail-panel > footer { align-items: stretch; flex-direction: column; }
}
</style>
